import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import {
  Lock, KeyRound, ShieldCheck, Fingerprint, MailCheck,
  LogOut, Monitor, CheckCircle2, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ez = mmEase.expo;

export default function Settings() {
  const { user, refresh, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [pin,            setPin           ] = useState("");
  const [pwd,            setPwd           ] = useState({ current: "", next: "" });
  const [busyAction,     setBusyAction    ] = useState(null);
  const [sessions,       setSessions      ] = useState(null);
  const [sessionsLoading,setSessionsLoading] = useState(true);
  const [deleteOpen,     setDeleteOpen    ] = useState(false);
  const [deleteForm,     setDeleteForm    ] = useState({ password: "", confirm: "" });
  const [deleteError,    setDeleteError   ] = useState(null);

  const webAuthnAvailable = typeof window !== "undefined" && !!window.PublicKeyCredential;

  useEffect(() => {
    api.get("/me/sessions")
      .then(({ data }) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  const setPinHandler = async () => {
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    setBusyAction("pin");
    try {
      await api.post("/me/set-pin", { pin });
      toast.success("Transaction PIN saved");
      setPin(""); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save PIN. Try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const changePassword = async () => {
    if (pwd.next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusyAction("password");
    try {
      await api.post("/me/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password updated");
      setPwd({ current: "", next: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password. Try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const toggle2fa = async () => {
    setBusyAction("2fa");
    try {
      const { data } = await api.post("/me/toggle-2fa");
      toast.success(`2FA ${data.two_fa_enabled ? "enabled" : "disabled"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update 2FA. Try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const verifyEmail = async () => {
    setBusyAction("email");
    try {
      await api.post("/auth/verify-email/resend", { email: user.email });
      toast.success("Verification code sent — enter it on the next screen.");
      // The email contains a 6-digit code, not a link — send the user to the
      // screen that actually accepts it (same one used right after signup).
      navigate(`/verify-email?email=${encodeURIComponent(user.email)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email. Try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const verifyPhone = async () => {
    setBusyAction("phone");
    try {
      await api.post("/auth/verify-phone");
      toast.success("Verification SMS sent");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification SMS. Try again.");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteAccount = async () => {
    setDeleteError(null);
    setBusyAction("delete");
    try {
      await api.post("/me/delete-account", {
        current_password: deleteForm.password,
        confirm: deleteForm.confirm,
      });
      toast.success("Your account has been deleted.");
      setUser(null);
      navigate("/login", { replace: true });
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Could not delete account. Please try again.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Account</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">
          Security &amp; preferences
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Lock it down. Tune your alerts. Your wallet, your rules.</p>
      </motion.div>

      {/* ── 2FA ─────────────────────────────────────────────────── */}
      <SettingsSection index={0} icon={ShieldCheck} title="Two-factor authentication"
        desc="Add an extra layer of security to every sign-in." accent="#10B981"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-navy text-sm">{user?.two_fa_enabled ? "Enabled" : "Disabled"}</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {user?.two_fa_enabled
                ? "You'll be asked for a code on each sign-in."
                : "Highly recommended for sending money."}
            </p>
          </div>
          <button
            data-testid="settings-toggle-2fa"
            onClick={toggle2fa}
            disabled={busyAction === "2fa"}
            className={user?.two_fa_enabled ? "btn-ghost-dark" : "btn-primary"}
          >
            {busyAction === "2fa"
              ? <Loader2 size={13} className="animate-spin" />
              : (user?.two_fa_enabled ? "Disable" : "Enable 2FA")}
          </button>
        </div>
      </SettingsSection>

      {/* ── PIN ─────────────────────────────────────────────────── */}
      <SettingsSection index={1} icon={KeyRound} title="Transaction PIN"
        desc="A 4-6 digit code required for each transfer." accent="#7C3AED"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder={user?.has_pin ? "Update PIN" : "Choose your PIN"}
            data-testid="settings-pin-input"
            className="input-field tracking-[0.5em] text-center font-mono w-40"
          />
          <button data-testid="settings-pin-save" onClick={setPinHandler} disabled={busyAction === "pin"} className="btn-navy">
            {busyAction === "pin" ? <Loader2 size={13} className="animate-spin" /> : "Save PIN"}
          </button>
          {user?.has_pin && (
            <span className="badge-emerald">
              <CheckCircle2 size={9} /> PIN set
            </span>
          )}
        </div>
      </SettingsSection>

      {/* ── Password ─────────────────────────────────────────────── */}
      <SettingsSection index={2} icon={Lock} title="Change password"
        desc="Use 12+ characters with letters, numbers and symbols." accent="#06B6D4"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            type="password"
            placeholder="Current password"
            value={pwd.current}
            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            data-testid="settings-pwd-current"
            className="input-field"
          />
          <input
            type="password"
            placeholder="New password"
            value={pwd.next}
            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            data-testid="settings-pwd-next"
            className="input-field"
          />
        </div>
        <button data-testid="settings-pwd-save" onClick={changePassword} disabled={busyAction === "password"} className="btn-navy mt-3 inline-flex items-center gap-1.5">
          {busyAction === "password" ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : "Update password"}
        </button>
      </SettingsSection>

      {/* ── Verification ────────────────────────────────────────── */}
      <SettingsSection index={3} icon={MailCheck} title="Email & phone verification"
        desc="Verify contact details to receive security alerts." accent="#F59E0B"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <VerifyRow
            status={user?.email_verified}
            label={user?.email}
            sublabel="Email address"
            onVerify={verifyEmail}
            testid="settings-verify-email"
            busy={busyAction === "email"}
          />
          <VerifyRow
            status={user?.phone_verified}
            label={user?.phone}
            sublabel="Phone number"
            onVerify={verifyPhone}
            testid="settings-verify-phone"
            busy={busyAction === "phone"}
          />
        </div>
      </SettingsSection>

      {/* ── Biometric ───────────────────────────────────────────── */}
      <SettingsSection index={4} icon={Fingerprint} title="Biometric login"
        desc="Use Face ID or Touch ID on supported devices." accent="#10B981"
      >
        {webAuthnAvailable ? (
          <div>
            <button
              data-testid="settings-biometric"
              disabled
              aria-disabled="true"
              className="btn-ghost-dark opacity-60 cursor-not-allowed"
            >
              Set up biometric
            </button>
            <p className="text-[12px] text-muted-foreground mt-2">
              Biometric authentication setup is coming soon.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground bg-secondary/50 rounded-xl px-4 py-3">
            Biometric authentication is not supported on this device or browser.
          </p>
        )}
      </SettingsSection>

      {/* ── Sessions ────────────────────────────────────────────── */}
      <SettingsSection index={5} icon={Monitor} title="Devices & sessions"
        desc="Recent sign-in activity on your account." accent="#475569"
      >
        <div className="space-y-2">
          {sessionsLoading ? (
            <div className="skeleton h-14 rounded-xl" />
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-secondary/50 rounded-xl px-4 py-3">
              No active session information is currently available.
            </p>
          ) : (
            sessions.map((d, i) => (
              <div key={d.id || i} className="flex items-center justify-between bg-secondary/60 rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-navy">{d.device || d.user_agent || "Unknown device"}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {d.location || ""}{d.last_seen ? ` · ${d.last_seen}` : ""}
                  </p>
                </div>
                {d.current && <span className="badge-emerald">This device</span>}
              </div>
            ))
          )}
        </div>
      </SettingsSection>

      {/* ── Sign out ─────────────────────────────────────────────── */}
      <motion.div
        className="card-elevated p-5 border-red-100 bg-red-50/40"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: ez, delay: 0.3 }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-red-100 text-red-500">
              <LogOut size={17} strokeWidth={1.8} />
            </div>
            <div>
              <p className="font-bold text-navy text-sm">Sign out</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">End your current session on this device.</p>
            </div>
          </div>
          <button
            data-testid="settings-signout"
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-red-600 bg-red-100 hover:bg-red-500 hover:text-white px-5 py-2 rounded-full transition-all shrink-0"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </motion.div>

      {/* ── Delete account ──────────────────────────────────────── */}
      <motion.div
        className="card-elevated p-5 border-red-100 bg-red-50/40"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.38, ease: ez, delay: 0.34 }}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-red-100 text-red-500">
            <AlertTriangle size={17} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-navy text-sm">Delete account</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              Permanently deletes your account. Your profile and identity documents are
              anonymised immediately and your login is deactivated right away. This cannot be undone.
            </p>

            {!deleteOpen ? (
              <button
                data-testid="settings-delete-account-open"
                onClick={() => setDeleteOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-red-600 bg-red-100 hover:bg-red-500 hover:text-white px-5 py-2 rounded-full transition-all"
              >
                Delete my account
              </button>
            ) : (
              <div className="mt-3 space-y-2.5">
                <p className="text-[12px] text-red-700 font-semibold">
                  Enter your password and type DELETE to confirm.
                </p>
                <input
                  type="password"
                  placeholder="Current password"
                  value={deleteForm.password}
                  onChange={(e) => setDeleteForm({ ...deleteForm, password: e.target.value })}
                  data-testid="settings-delete-password"
                  className="input-field"
                />
                <input
                  type="text"
                  placeholder='Type "DELETE" to confirm'
                  value={deleteForm.confirm}
                  onChange={(e) => setDeleteForm({ ...deleteForm, confirm: e.target.value })}
                  data-testid="settings-delete-confirm"
                  className="input-field"
                />
                {deleteError && (
                  <p className="text-[12px] text-red-600 font-semibold">{deleteError}</p>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    data-testid="settings-delete-submit"
                    onClick={deleteAccount}
                    disabled={
                      busyAction === "delete" ||
                      deleteForm.confirm !== "DELETE" ||
                      deleteForm.password.length === 0
                    }
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2 rounded-full transition-all"
                  >
                    {busyAction === "delete"
                      ? <><Loader2 size={13} className="animate-spin" /> Deleting…</>
                      : "Permanently delete"}
                  </button>
                  <button
                    onClick={() => { setDeleteOpen(false); setDeleteError(null); setDeleteForm({ password: "", confirm: "" }); }}
                    disabled={busyAction === "delete"}
                    className="text-sm font-semibold text-muted-foreground px-4 py-2 rounded-full hover:bg-secondary/60 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function SettingsSection({ icon: Icon, title, desc, children, accent, index }) {
  return (
    <motion.div
      className="card-elevated p-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: ez, delay: 0.06 + index * 0.04 }}
    >
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
          style={{ background: `${accent}14`, color: accent }}
        >
          <Icon size={17} strokeWidth={1.8} />
        </div>
        <div>
          <p className="font-bold text-navy text-sm">{title}</p>
          <p className="text-[12px] text-muted-foreground mt-0.5">{desc}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );
}

function VerifyRow({ status, label, sublabel, onVerify, testid, busy }) {
  return (
    <div className="flex items-center justify-between bg-secondary/60 rounded-xl px-4 py-3">
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground font-bold">{sublabel}</p>
        <p className="text-[13px] font-semibold text-navy truncate">{label || "—"}</p>
      </div>
      {status
        ? <span className="badge-emerald ml-2 shrink-0">Verified</span>
        : <button data-testid={testid} onClick={onVerify} disabled={busy}
            className="ml-2 inline-flex items-center gap-1.5 text-[11px] font-bold bg-navy text-white hover:bg-navy-light px-3 py-1.5 rounded-full transition-colors shrink-0 disabled:opacity-60">
            {busy ? <Loader2 size={11} className="animate-spin" /> : "Verify"}
          </button>
      }
    </div>
  );
}
